/**
 * Lead detail (S-A21).
 *
 * !! THE LEADS ENDPOINTS DO NOT EXIST YET !! Runs against MSW. The action buttons are
 * rendered from `_actions` and are wired to nothing until the CRM agent lands the
 * sub-resources (`assign`, `stage`, `convert`).
 *
 * A domain note carried in the layout: conversion does not replace the lead. The lead
 * survives and is linked to the client it produced (BR-12), because how a client
 * arrived is part of the client relationship. So a converted lead still opens here
 * and still shows its own history.
 */

import { ConflictError, qk, useApi, useApiMutation, useApiQuery, type Lead } from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Skeleton,
  useToast,
} from '@legalcrm/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { STAFF } from '@legalcrm/api';
import { Can } from '@/auth/permissions';
import { formatDateTime, formatMoney, humanise } from '@/lib/format';
import { DeleteWithReasonDialog } from '@/components/DeleteWithReasonDialog';
import { ConflictCheckPanel } from '@/routes/cases/ConflictCheckPanel';
import { ConvertLeadDialog } from './ConvertLeadDialog';
import { LeadServicesCard } from './LeadServicesCard';
import { SendEngagementDialog } from './SendEngagementDialog';
import { CustomFieldsSummary } from '@/components/customFields/CustomFields';
import { PageHeader } from '@/shell/PageHeader';
import { useAuth } from '@/auth/AuthProvider';

export function LeadDetailPage() {
  const { leadId = '' } = useParams();
  const { baseCurrency } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [engaging, setEngaging] = useState(false);

  const query = useApiQuery<Lead>(qk.detail('leads', leadId), STAFF.lead(leadId), {
    enabled: Boolean(leadId),
  });

  // Above the early returns: a hook below a conditional `return` changes hook order
  // between renders and crashes on the render where the data arrives.
  const remove = useApiMutation<unknown, string>(
    {
      mutationFn: (reason) => api.delete(STAFF.lead(leadId), { body: { reason } }),
      invalidates: [qk.all('leads'), qk.dashboard()],
    },
    { onSuccess: () => navigate('/leads') },
  );

  // The move `ConflictCheckPanel` exists to unblock. The server refuses this with a 409
  // naming exactly one of two reasons -- no decided conflict check, or (for `lost`,
  // which this button never sends) no reason -- so the refusal is shown as-is rather
  // than reworded into something guessed.
  const qualify = useApiMutation<Lead, void>(
    {
      mutationFn: () => api.post<Lead>(`${STAFF.leadStage(leadId)}/`, { to_stage: 'qualified' }),
      invalidates: [qk.detail('leads', leadId), qk.all('leads'), qk.dashboard()],
    },
    {
      onSuccess: () => toast({ title: 'Qualified', tone: 'success' }),
      onError: (error) =>
        toast({
          title: 'Not qualified yet',
          description: error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        }),
    },
  );

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <span className="sr-only">Loading lead</span>
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (query.error || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const lead = query.data;

  return (
    <>
      <PageHeader
        title={lead.full_name}
        description={lead.enquiry_text || undefined}
        meta={
          <>
            <span className="font-mono text-sm text-ink-muted">{lead.reference}</span>
            <Badge tone="info">{lead.stage.name}</Badge>
            {lead.priority === 'high' && (
              <Badge tone="warning" size="sm">
                High priority
              </Badge>
            )}
            {!lead.first_response_at && (
              <Badge tone="danger" size="sm">
                Not yet contacted
              </Badge>
            )}
            {lead.converted_client_id && (
              <Badge tone="success" size="sm">
                Converted
              </Badge>
            )}
          </>
        }
        actions={
          <>
            <Can actions={lead._actions} action="edit">
              <Button variant="secondary">Log activity</Button>
            </Can>
            {/*
              `lead.edit`, same reasoning as Convert below: there is no
              `lead.transition` permission, stage moves are governed by `lead.edit`, and
              the server re-checks it regardless of what this draws. Hidden once already
              qualified, lost or converted -- there is no stage this button sends the
              lead to from any of those, and the server would refuse it anyway.
            */}
            {!lead.converted_client_id &&
              lead.stage.stage_type === 'open' &&
              lead.stage.key !== 'qualified' && (
                <Can actions={lead._actions} action="edit">
                  <Button
                    variant="secondary"
                    loading={qualify.isPending}
                    onClick={() => qualify.mutate()}
                  >
                    Qualify
                  </Button>
                </Can>
              )}
            {/*
              The step between qualifying an enquiry and converting it. Hidden once
              converted: the agreement moves to the client at that point, and raising a
              second one against a spent lead would attach it to nothing.
            */}
            {!lead.converted_client_id && (
              <Can actions={lead._actions} action="edit">
                <Button variant="secondary" onClick={() => setEngaging(true)}>
                  Engagement letter
                </Button>
              </Can>
            )}
            {/*
              `edit`, not `transition`. There is no `lead.transition` permission in the
              catalogue -- lead stage moves are governed by `lead.edit` -- so guarding on
              one hid this control from everybody, which is how it came to be a disabled
              placeholder in the first place.

              The server additionally requires `client.create`, and re-checks both. This
              decides what to draw; it never decides what is allowed.
            */}
            <Can actions={lead._actions} action="edit">
              {lead.converted_client_id ? (
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/clients/${lead.converted_client_id}`)}
                >
                  Open the client
                </Button>
              ) : (
                <Button variant="primary" onClick={() => setConverting(true)}>
                  Convert to client
                </Button>
              )}
            </Can>
            <Can module="lead" action="delete">
              <Button variant="ghost" onClick={() => setDeleting(true)}>
                Delete
              </Button>
            </Can>
          </>
        }
      />

      <SendEngagementDialog
        lead={lead}
        open={engaging}
        onClose={() => setEngaging(false)}
      />

      <ConvertLeadDialog
        leadId={lead.id}
        open={converting}
        onClose={() => setConverting(false)}
      />

      <DeleteWithReasonDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        label={lead.reference ?? 'this lead'}
        noun="lead"
        caveat="A lead that came to nothing should be marked lost or not qualified instead — that keeps it in the reporting."
        busy={remove.isPending}
        error={remove.error}
        onConfirm={(reason) => remove.mutateAsync(reason)}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Enquiry" />
          <CardBody>
            {lead.enquiry_text ? (
              <p className="whitespace-pre-line text-base text-ink">{lead.enquiry_text}</p>
            ) : (
              <p className="text-sm text-ink-subtle">No enquiry detail recorded.</p>
            )}
          </CardBody>
        </Card>

        <div className="flex flex-col gap-4">
          {/*
            Above Details, same reasoning as the services card below it: this is the
            decision that unlocks qualifying the lead, and it used to be unsatisfiable
            from here at all -- `subject_type: 'lead'` reached the same generic gate
            (`conflict.services.require_cleared`) that a matter's client already used,
            but no screen could run a check against a lead, which had no client yet.
            Hidden once converted: the check that matters from that point on is the
            client's, run from the matter, not the spent lead's.
          */}
          {!lead.converted_client_id && (
            <ConflictCheckPanel
              subjectType="lead"
              subjectId={lead.id}
              subjectName={lead.full_name}
              requirementLabel="this enquiry can be qualified"
            />
          )}

          {/*
            Above Details on purpose. It is the decision that unlocks conversion, and
            burying it under nine read-only rows is how it stays unset.
          */}
          <LeadServicesCard lead={lead} />

        <Card>
          <CardHeader title="Details" />
          <CardBody>
            <dl className="flex flex-col gap-3">
              <Row label="Type">
                {lead.lead_type === 'organization' ? 'Organization' : 'Individual'}
              </Row>
              <Row label="Telephone">{lead.phone || '—'}</Row>
              <Row label="Email">{lead.email || '—'}</Row>
              <Row label="Location">{lead.location || '—'}</Row>
              <Row label="Source">{lead.source?.name ?? '—'}</Row>
              <Row label="Owner">
                {lead.assigned_user ? (
                  lead.assigned_user.full_name
                ) : (
                  <Badge tone="danger" size="sm">
                    Unassigned
                  </Badge>
                )}
              </Row>
              <Row label="Expected value">
                {formatMoney(lead.expected_value, lead.currency || baseCurrency)}
              </Row>
              <Row label="Priority">{humanise(lead.priority)}</Row>
              <Row label="First response">
                {lead.first_response_at ? formatDateTime(lead.first_response_at) : 'Not yet'}
              </Row>
              <Row label="Created">{formatDateTime(lead.created_at)}</Row>
              {lead.converted_client_id && (
                <Row label="Client">
                  <Link
                    to={`/clients/${lead.converted_client_id}`}
                    className="text-primary hover:underline"
                  >
                    View client record
                  </Link>
                </Row>
              )}
            </dl>

            {/*
              The firm's own fields, in their own block under the built-in ones rather
              than mixed in with them. Somebody reading this panel should be able to
              tell at a glance which values every firm has and which this firm invented,
              because the second kind is the one whose meaning has to be asked about.

              Renders nothing when the firm has added none.
            */}
            <CustomFieldsSummary entity="lead" values={lead.custom_fields} />
          </CardBody>
        </Card>
        </div>
      </div>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="text-right text-base text-ink">{children}</dd>
    </div>
  );
}
